import { useEffect, useRef, useState } from 'react'
import {
  detectAudioContainerFromBytes,
  ensureAudioBlobType,
  getAudioContainerFromMimeType,
  normalizeRecordingBlob,
  stitchBlobsToWav,
} from '@/lib/audio'
import { deleteDraft, deleteSubmission, getChunksBySession } from '@/lib/db'
import { getSubmissionAudioUrl } from '@/lib/api'
import { syncAll } from '@/lib/sync'
import { useToast } from '@/components/providers/ToastProvider'
import { copy, syncSuccessMessage } from '@/copy/strings'
import type { DraftRecording, Submission } from '@/types'
import { getDisplayDate } from '../formatters'

// Resolve once the element has buffered enough to play, reject on error/timeout.
// Used for both local blob URLs and remote (presigned S3) sources.
function awaitCanPlay(audioEl: HTMLAudioElement, timeoutMs = 10000): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer)
      audioEl.removeEventListener('canplay', onReady)
      audioEl.removeEventListener('canplaythrough', onReady)
      audioEl.removeEventListener('error', onError)
    }
    const onReady = () => {
      cleanup()
      resolve()
    }
    const onError = () => {
      cleanup()
      const err = audioEl.error
      reject(new Error(err ? `Code ${err.code}: ${err.message || 'load error'}` : 'Audio failed to load'))
    }
    const timer = setTimeout(() => {
      cleanup()
      reject(new Error('Audio load timeout'))
    }, timeoutMs)
    audioEl.addEventListener('canplay', onReady, { once: true })
    audioEl.addEventListener('canplaythrough', onReady, { once: true })
    audioEl.addEventListener('error', onError, { once: true })
  })
}

interface UsePlaybackSessionInput {
  recording: DraftRecording | Submission
  isDraft: boolean
  blobSource: Blob | null | undefined
  hasChunkSource: boolean
  isRemoteAudio: boolean
  chunkSessionId: string | null
  canDownloadAudio: boolean
  playbackBlob: Blob | null
  setPlaybackBlob: (blob: Blob | null) => void
  setPlaybackSupported: (supported: boolean) => void
  onPlaybackStart?: (id: string) => void
  onPlaybackStop?: (id: string) => void
  onDelete: () => void
  currentPlayingId?: string | null
}

export function usePlaybackSession({
  recording,
  isDraft,
  blobSource,
  hasChunkSource,
  isRemoteAudio,
  chunkSessionId,
  canDownloadAudio,
  playbackBlob,
  setPlaybackBlob,
  setPlaybackSupported,
  onPlaybackStart,
  onPlaybackStop,
  onDelete,
  currentPlayingId,
}: UsePlaybackSessionInput) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [playbackError, setPlaybackError] = useState<string | null>(null)
  const [isPreparingPlayback, setIsPreparingPlayback] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const activeBlobUrlRef = useRef<string | null>(null)
  // Cached presigned S3 URL for remote playback; refetched on expiry (403).
  const remoteUrlRef = useRef<string | null>(null)
  const hasUserInitiatedPlayRef = useRef(false)
  const { showToast } = useToast()

  const logPlaybackEvent = (eventName: string, extra?: Record<string, unknown>) => {
    const audioEl = audioRef.current
    console.log('[PLAYBACK]', {
      event: eventName,
      recordingId: recording.id,
      currentTime: audioEl?.currentTime,
      duration: audioEl?.duration,
      readyState: audioEl?.readyState,
      networkState: audioEl?.networkState,
      paused: audioEl?.paused,
      ended: audioEl?.ended,
      src: audioEl?.src,
      audioUrl,
      hasPlaybackBlob: !!playbackBlob,
      playbackBlobSize: playbackBlob?.size,
      playbackBlobType: playbackBlob?.type,
      ...extra,
    })
  }

  // Build a blob URL whenever playbackBlob changes.
  useEffect(() => {
    let cancelled = false

    const buildUrl = () => {
      console.log('[TESTING] buildUrl called', {
        hasPlaybackBlob: !!playbackBlob,
        playbackBlobType: playbackBlob?.type,
        playbackBlobSize: playbackBlob?.size,
      })

      if (!playbackBlob) {
        console.log('[TESTING] No playbackBlob, setting audioUrl to null')
        if (!cancelled) {
          setAudioUrl(null)
        }
        activeBlobUrlRef.current = null
        return
      }

      if (activeBlobUrlRef.current) {
        console.log('[PLAYBACK] Reusing existing blob URL', { url: activeBlobUrlRef.current })
        if (!cancelled) {
          setAudioUrl(activeBlobUrlRef.current)
        }
        return
      }

      try {
        if (playbackBlob.size === 0) {
          console.warn('[TESTING] Playback blob has zero size!')
        }

        const url = URL.createObjectURL(playbackBlob)
        console.log('[TESTING] Created blob URL', {
          url,
          blobType: playbackBlob.type,
          blobSize: playbackBlob.size,
        })
        activeBlobUrlRef.current = url
        if (!cancelled) {
          setAudioUrl(url)
          console.log('[TESTING] Set audioUrl state', { url })
        }
      } catch (error) {
        console.warn('[TESTING] Failed to create audio URL', error)
        if (!cancelled) {
          setAudioUrl(null)
        }
      }
    }

    buildUrl()

    return () => {
      cancelled = true
      // Safari can aggressively purge blob URLs; keep URL alive for component lifetime.
    }
  }, [playbackBlob])

  // Reset elapsed indicator when a fresh blob is loaded.
  useEffect(() => {
    setElapsedSeconds(0)
  }, [audioUrl, playbackBlob])

  const ensureActiveBlobUrl = (reason: string): string | null => {
    if (!playbackBlob) {
      console.log('[PLAYBACK] No playback blob available', { reason })
      setAudioUrl(null)
      return null
    }

    const current = activeBlobUrlRef.current || audioUrl
    if (current && current.startsWith('blob:')) {
      logPlaybackEvent('blob-url-valid', { reason, url: current })
      return current
    }

    try {
      const freshUrl = URL.createObjectURL(playbackBlob)
      activeBlobUrlRef.current = freshUrl
      setAudioUrl(freshUrl)
      logPlaybackEvent('blob-url-created', { reason, url: freshUrl })
      return freshUrl
    } catch (error) {
      console.warn('[PLAYBACK] Failed to create blob URL', { reason, error })
      setPlaybackError('Unable to load audio. Please try again.')
      return null
    }
  }

  const attemptResumePlayback = async (reason: string) => {
    if (isRemoteAudio) {
      // Remote streaming buffers natively; never tear down the presigned src.
      return
    }
    if (!hasUserInitiatedPlayRef.current) {
      logPlaybackEvent('resume-skipped', { reason, hasUserInitiatedPlay: false })
      return
    }

    const url = ensureActiveBlobUrl(reason)
    if (!audioRef.current || !url) {
      return
    }

    if (audioRef.current.src !== url) {
      logPlaybackEvent('rebinding-src', { reason, oldSrc: audioRef.current.src, newSrc: url })
      audioRef.current.src = url
      audioRef.current.load()
    }

    try {
      await audioRef.current.play()
      setIsPlaying(true)
      logPlaybackEvent('resume-success', { reason })
    } catch (resumeError) {
      console.warn('[PLAYBACK] Failed to resume playback', resumeError)
      // Do not surface UI error for transient autoplay blocks; user can tap play.
    }
  }

  // Wire up audio element event listeners.
  useEffect(() => {
    const audioEl = audioRef.current
    if (!audioEl) {
      return
    }

    const handleEvent = (event: Event) => {
      const type = event.type
      logPlaybackEvent(type)

      if (type === 'waiting' || type === 'stalled' || type === 'suspend') {
        void attemptResumePlayback(`event:${type}`)
      }

      if (type === 'timeupdate' || type === 'seeked') {
        setElapsedSeconds(audioEl.currentTime || 0)
      }

      if (type === 'error') {
        const error = audioEl.error
        setPlaybackError('Playback interrupted. Tap retry.')
        console.warn('[PLAYBACK] Audio error event', {
          code: error?.code,
          message: error?.message,
          src: audioEl.src,
        })
      }
    }

    const eventNames: Array<keyof HTMLMediaElementEventMap> = [
      'play',
      'pause',
      'timeupdate',
      'waiting',
      'stalled',
      'suspend',
      'ended',
      'error',
      'canplay',
      'canplaythrough',
      'seeking',
      'seeked',
      'loadedmetadata',
      'progress',
    ]

    eventNames.forEach((name) => audioEl.addEventListener(name, handleEvent))
    return () => {
      eventNames.forEach((name) => audioEl.removeEventListener(name, handleEvent))
    }
  }, [audioUrl, playbackBlob])

  // Enforce single-playback: if another recording started playing, stop this one.
  useEffect(() => {
    if (!isPlaying) {
      return
    }
    if (!currentPlayingId || currentPlayingId === recording.id) {
      return
    }
    const audioEl = audioRef.current
    if (audioEl) {
      audioEl.pause()
      audioEl.currentTime = 0
    }
    hasUserInitiatedPlayRef.current = false
    setIsPlaying(false)
    setPlaybackError(null)
    setElapsedSeconds(0)
    // Intentionally do NOT call onPlaybackStop here — the parent's
    // currentPlayingId already reflects the newly started recording, and
    // calling it would clear that state and unlock list reloads prematurely.
  }, [currentPlayingId, isPlaying, recording.id])

  const prepareChunkPlayback = async (): Promise<Blob | null> => {
    if (!hasChunkSource || !chunkSessionId || isPreparingPlayback) {
      return null
    }

    setIsPreparingPlayback(true)
    try {
      const chunks = await getChunksBySession(chunkSessionId)
      if (chunks.length === 0) {
        setPlaybackError('Playback unavailable. Try reloading the page.')
        return null
      }

      const chunkBlobs = chunks
        .map((chunk) => chunk.blob)
        .filter((blob): blob is Blob => Boolean(blob))
        .map((blob) => ensureAudioBlobType(blob))

      if (chunkBlobs.length === 0) {
        setPlaybackError('Playback unavailable. Try reloading the page.')
        return null
      }

      const stitched = await stitchBlobsToWav(chunkBlobs)
      setPlaybackSupported(true)
      setPlaybackBlob(stitched)
      return stitched
    } catch (error) {
      console.warn('Failed to prepare chunk playback', {
        recordingId: recording.id,
        error: error instanceof Error ? error.message : String(error),
      })
      setPlaybackError('Playback unavailable. Try reloading the page.')
      return null
    } finally {
      setIsPreparingPlayback(false)
    }
  }

  // Resolve (and cache) a presigned S3 URL for remote playback/download.
  const fetchRemoteAudioUrl = async (forceRefresh = false): Promise<string | null> => {
    if (!forceRefresh && remoteUrlRef.current) {
      return remoteUrlRef.current
    }
    try {
      const { url } = await getSubmissionAudioUrl(recording.id)
      remoteUrlRef.current = url
      return url
    } catch (error) {
      console.warn('Failed to fetch remote audio URL', {
        recordingId: recording.id,
        error: error instanceof Error ? error.message : String(error),
      })
      return null
    }
  }

  // Stream the recording from S3 once its local copy has been purged. The native
  // <audio> element loads the presigned URL directly (no CORS needed for media).
  const playRemoteAudio = async (isRetry = false): Promise<void> => {
    if (!navigator.onLine) {
      setPlaybackError(copy.playbackOfflineRemote)
      onPlaybackStop?.(recording.id)
      return
    }
    const audioEl = audioRef.current
    if (!audioEl) return

    const url = await fetchRemoteAudioUrl(isRetry)
    if (!url) {
      setPlaybackError('Unable to load audio. Please try again.')
      onPlaybackStop?.(recording.id)
      return
    }

    try {
      setAudioUrl(url)
      if (audioEl.src !== url) {
        audioEl.src = url
      }
      audioEl.load()
      await awaitCanPlay(audioEl)
      await audioEl.play()
      setIsPlaying(true)
      hasUserInitiatedPlayRef.current = true
      setPlaybackError(null)
      setElapsedSeconds(audioEl.currentTime || 0)
      onPlaybackStart?.(recording.id)
    } catch (error) {
      // A presigned URL can 403 after its TTL — refetch once and retry.
      if (!isRetry) {
        remoteUrlRef.current = null
        await playRemoteAudio(true)
        return
      }
      console.warn('Remote playback failed', {
        recordingId: recording.id,
        error: error instanceof Error ? error.message : String(error),
      })
      setPlaybackError('Playback interrupted. Tap retry.')
      onPlaybackStop?.(recording.id)
    }
  }

  const handlePlay = async () => {
    setPlaybackError(null)
    // Signal intent to play immediately so any other currently-playing
    // recording stops before this one actually begins playback.
    onPlaybackStart?.(recording.id)

    // Remote (S3) playback: no local bytes or chunks, stream via presigned URL.
    if (!playbackBlob && !hasChunkSource && isRemoteAudio) {
      await playRemoteAudio()
      return
    }

    const activeBlob = playbackBlob ?? (hasChunkSource ? await prepareChunkPlayback() : null)
    console.log('[TESTING] handlePlay called', {
      hasAudioRef: !!audioRef.current,
      hasPlaybackBlob: !!activeBlob,
      playbackBlobType: activeBlob?.type,
      playbackBlobSize: activeBlob?.size,
      currentAudioUrl: audioUrl,
      audioElementSrc: audioRef.current?.src,
      audioElementReadyState: audioRef.current?.readyState,
    })

    if (!audioRef.current || !activeBlob) {
      console.log('[TESTING] Cannot play - missing audioRef or playbackBlob')
      return
    }

    try {
      let currentUrl = audioUrl
      console.log('[TESTING] Checking blob URL', {
        currentUrl,
        isValidBlobUrl: currentUrl?.startsWith('blob:'),
      })

      if (!currentUrl || !currentUrl.startsWith('blob:')) {
        console.log('[TESTING] Creating new blob URL')
        if (currentUrl && currentUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentUrl)
        }
        currentUrl = URL.createObjectURL(activeBlob)
        console.log('[TESTING] Created new blob URL', {
          url: currentUrl,
          blobType: activeBlob.type,
          blobSize: activeBlob.size,
        })
        setAudioUrl(currentUrl)
        // Wait for state update
        await new Promise((resolve) => setTimeout(resolve, 50))
      }

      if (audioRef.current.src !== currentUrl) {
        console.log('[TESTING] Updating audio element src', {
          oldSrc: audioRef.current.src,
          newSrc: currentUrl,
        })
        audioRef.current.src = currentUrl
      }

      // Browsers do not support HEAD requests for blob URLs.
      if (currentUrl.startsWith('blob:')) {
        console.log('[TESTING] Skipping HEAD fetch probe for blob URL', {
          url: currentUrl,
          blobType: activeBlob.type,
          blobSize: activeBlob.size,
        })
      } else {
        try {
          const testResponse = await fetch(currentUrl, { method: 'HEAD' })
          console.log('[TESTING] URL fetch test', {
            url: currentUrl,
            status: testResponse.status,
            contentType: testResponse.headers.get('content-type'),
            contentLength: testResponse.headers.get('content-length'),
          })
        } catch (fetchError) {
          console.warn('[TESTING] URL fetch test failed', fetchError)
        }
      }

      try {
        const blobArrayBuffer = await activeBlob.arrayBuffer()
        const headerBytes = new Uint8Array(blobArrayBuffer.slice(0, 12))
        const headerString = String.fromCharCode(...headerBytes.slice(0, 4))
        const headerHex = Array.from(headerBytes).map(b => b.toString(16).padStart(2, '0')).join(' ')
        const declaredContainer = getAudioContainerFromMimeType(activeBlob.type)
        const detectedContainer = detectAudioContainerFromBytes(headerBytes)
        const hasContainerMismatch =
          declaredContainer !== 'unknown' &&
          detectedContainer !== 'unknown' &&
          declaredContainer !== detectedContainer

        console.log('[TESTING] Blob data validation before load', {
          blobSize: activeBlob.size,
          blobType: activeBlob.type,
          arrayBufferSize: blobArrayBuffer.byteLength,
          headerString,
          headerHex,
          declaredContainer,
          detectedContainer,
          hasContainerMismatch,
          first12Bytes: Array.from(headerBytes),
        })

        if (declaredContainer === 'wav' && detectedContainer !== 'unknown' && detectedContainer !== 'wav') {
          console.warn('[TESTING] Declared WAV blob has mismatched container', {
            expectedContainer: 'wav',
            declaredContainer,
            detectedContainer,
            blobType: activeBlob.type,
            headerHex,
          })
        } else if (detectedContainer === 'wav') {
          const waveChunk = String.fromCharCode(...headerBytes.slice(8, 12))
          console.log('[TESTING] WAV chunk identifier', { waveChunk, isValid: waveChunk === 'WAVE' })
        }
      } catch (blobValidationError) {
        console.error('[TESTING] Failed to validate blob data', blobValidationError)
      }

      console.log('[TESTING] Loading audio element', {
        src: audioRef.current.src,
        readyState: audioRef.current.readyState,
      })
      audioRef.current.load()

      await new Promise<void>((resolve, reject) => {
        if (!audioRef.current) {
          reject(new Error('Audio element not available'))
          return
        }

        const timeout = setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.removeEventListener('canplay', handleCanPlay)
            audioRef.current.removeEventListener('canplaythrough', handleCanPlayThrough)
            audioRef.current.removeEventListener('error', handleError)
          }
          reject(new Error('Audio load timeout'))
        }, 10000)

        const handleCanPlay = () => {
          clearTimeout(timeout)
          if (audioRef.current) {
            audioRef.current.removeEventListener('canplay', handleCanPlay)
            audioRef.current.removeEventListener('canplaythrough', handleCanPlayThrough)
            audioRef.current.removeEventListener('error', handleError)
          }
          resolve()
        }

        const handleCanPlayThrough = () => {
          clearTimeout(timeout)
          if (audioRef.current) {
            audioRef.current.removeEventListener('canplay', handleCanPlay)
            audioRef.current.removeEventListener('canplaythrough', handleCanPlayThrough)
            audioRef.current.removeEventListener('error', handleError)
          }
          resolve()
        }

        const handleError = () => {
          clearTimeout(timeout)
          if (audioRef.current) {
            audioRef.current.removeEventListener('canplay', handleCanPlay)
            audioRef.current.removeEventListener('canplaythrough', handleCanPlayThrough)
            audioRef.current.removeEventListener('error', handleError)
          }
          const error = audioRef.current?.error
          const errorMessage = error
            ? `Code: ${error.code}, Message: ${error.message || 'Unknown error'}`
            : 'Audio element failed to load'
          console.warn('Audio element error', { error, errorMessage, blobSize: playbackBlob?.size, blobType: playbackBlob?.type })
          setPlaybackError('Playback interrupted. Tap retry.')
          reject(new Error(errorMessage))
        }

        audioRef.current.addEventListener('canplay', handleCanPlay, { once: true })
        audioRef.current.addEventListener('canplaythrough', handleCanPlayThrough, { once: true })
        audioRef.current.addEventListener('error', handleError, { once: true })
      })

      await audioRef.current.play()
      setIsPlaying(true)
      hasUserInitiatedPlayRef.current = true
      setPlaybackError(null)
      setElapsedSeconds(audioRef.current.currentTime || 0)
      onPlaybackStart?.(recording.id)
    } catch (error) {
      console.warn('Playback failed', error)
      // If playback fails, recreate blob URL and retry
      if (playbackBlob && playbackBlob.size > 0) {
        try {
          if (audioUrl && audioUrl.startsWith('blob:')) {
            URL.revokeObjectURL(audioUrl)
          }
          const newUrl = URL.createObjectURL(playbackBlob)
          setAudioUrl(newUrl)

          await new Promise((resolve) => setTimeout(resolve, 300))
          if (audioRef.current) {
            audioRef.current.src = newUrl
            audioRef.current.load()
            await new Promise((resolve) => setTimeout(resolve, 200))
            await audioRef.current.play()
            setIsPlaying(true)
            hasUserInitiatedPlayRef.current = true
            setPlaybackError(null)
            setElapsedSeconds(audioRef.current.currentTime || 0)
            onPlaybackStart?.(recording.id)
          }
        } catch (retryError) {
          console.warn('Retry playback failed', retryError)
          setPlaybackError('Playback interrupted. Tap retry.')
        }
      } else {
        console.warn('Cannot retry playback - invalid blob', { blobSize: playbackBlob?.size })
        setPlaybackError('Playback unavailable. Try reloading the page.')
      }
    }
  }

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setIsPlaying(false)
      setPlaybackError(null)
      setElapsedSeconds(0)
      onPlaybackStop?.(recording.id)
    }
  }

  const handleDelete = async () => {
    const itemType = isDraft ? 'recording' : 'note'
    if (window.confirm(`Are you sure you want to delete this ${itemType}?`)) {
      if (isDraft) {
        await deleteDraft(recording.id)
      } else {
        await deleteSubmission(recording.id)
      }
      handleStop()
      onDelete()
      window.dispatchEvent(new Event('recording-saved'))
    }
  }

  const buildDownloadFilename = () => {
    const rawDate = getDisplayDate(recording.createdAt)
    const sanitized = rawDate.replace(/[^0-9A-Za-z]+/g, '-').replace(/^-+|-+$/g, '')
    return `recording-${sanitized || Date.now()}.wav`
  }

  // Download a purged recording by fetching its presigned S3 URL's bytes.
  // (Reading the bytes cross-origin needs S3 CORS to allow the app origin.)
  const downloadRemoteAudio = async (): Promise<void> => {
    if (!navigator.onLine) {
      showToast(copy.playbackOfflineRemote, 'warning')
      return
    }
    const url = await fetchRemoteAudioUrl(true)
    if (!url) {
      showToast('Failed to download recording. Please try again.', 'error')
      return
    }
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Download request failed: ${response.status}`)
    }
    const remoteBlob = await response.blob()
    const normalized = await normalizeRecordingBlob(remoteBlob)
    const objectUrl = URL.createObjectURL(normalized.blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = buildDownloadFilename()
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  }

  const handleDownload = async () => {
    if (!canDownloadAudio) {
      showToast('No audio available to download', 'warning')
      return
    }

    // Remote download: the local copy was purged, fetch from S3 instead.
    if (!playbackBlob && !hasChunkSource && isRemoteAudio) {
      try {
        await downloadRemoteAudio()
      } catch (error) {
        console.warn('Failed to download remote recording', error)
        showToast('Failed to download recording. Please try again.', 'error')
      }
      return
    }

    try {
      const sourceBlob = playbackBlob ?? (hasChunkSource ? await prepareChunkPlayback() : blobSource)
      if (!sourceBlob) {
        showToast('No audio available to download', 'warning')
        return
      }
      const normalized = await normalizeRecordingBlob(sourceBlob)
      const url = URL.createObjectURL(normalized.blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = buildDownloadFilename()
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (error) {
      console.warn('Failed to download recording', error)
      showToast('Failed to download recording. Please try again.', 'error')
    }
  }

  const handleEnded = () => {
    setIsPlaying(false)
    setPlaybackError(null)
    setElapsedSeconds(0)
    onPlaybackStop?.(recording.id)
  }

  const handleRetrySync = async () => {
    try {
      showToast(copy.syncStarted, 'info', 2000)
      const result = await syncAll()
      if (result.success && result.synced > 0) {
        showToast(syncSuccessMessage(result.synced), 'success', 3000)
      } else if (result.success && result.synced === 0) {
        showToast(copy.syncAllAlreadySynced, 'info', 2000)
      } else {
        // Raw server errors are logged via console.error rather than surfaced
        // in a toast that users can't act on.
        if (result.errors.length > 0) {
          console.error('[sync] failed with errors', result.errors)
        }
        showToast(copy.syncFailedShort, 'error', 4000)
      }
    } catch (error) {
      console.error('Retry sync failed', error)
      showToast(copy.syncFailedShort, 'error', 4000)
    }
  }

  const handleAudioElementError = () => {
    const error = audioRef.current?.error
    setPlaybackError('Playback interrupted. Tap retry.')
    console.warn('Audio element error', {
      error,
      errorCode: error?.code,
      errorMessage: error?.message,
      src: audioRef.current?.src,
      blobSize: playbackBlob?.size,
      blobType: playbackBlob?.type,
    })
  }

  return {
    audioRef,
    audioUrl,
    isPlaying,
    isPreparingPlayback,
    playbackError,
    elapsedSeconds,
    setIsPlaying,
    setPlaybackError,
    handlePlay,
    handleStop,
    handleDelete,
    handleDownload,
    handleEnded,
    handleRetrySync,
    handleAudioElementError,
  }
}
